import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import type { CountSessionDetailDto, CountSessionDto, LocationDto, WarehouseDto } from '@thebb/shared';
import { get, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';

const statusLabel: Record<string, string> = { open: 'Åben', counted: 'Talt', posted: 'Bogført', cancelled: 'Annulleret' };
const statusTone = (s: string) => (s === 'open' ? 'amber' : s === 'posted' ? 'green' : 'zinc') as 'amber' | 'green' | 'zinc';

/** Count sessions: cycle counts and the opening count that migrates stock into the system. */
export function CountsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  const list = useQuery({ queryKey: ['counts'], queryFn: () => get<CountSessionDto[]>('/counts'), refetchInterval: 30_000 });
  return (
    <div>
      <PageTitle title="Optælling" actions={can('stock.count') && <button className="btn btn-primary" onClick={() => setCreating(true)}>Ny optælling</button>} />
      <ErrorBox error={error} />
      {list.isLoading ? (
        <Spinner />
      ) : (
        <table className="table card p-0">
          <thead>
            <tr><th>Navn</th><th>Lager</th><th>Omfang</th><th>Art</th><th>Status</th><th className="text-right">Linjer</th><th className="text-right">Talt</th><th className="text-right">Afvigelser</th><th>Oprettet</th><th>Bogført</th></tr>
          </thead>
          <tbody>
            {list.data?.map((s) => (
              <tr key={s.id}>
                <td><Link to="/counts/$id" params={{ id: s.id }} className="font-medium text-blue-700 hover:underline">{s.name}</Link></td>
                <td>{s.warehouseCode}</td>
                <td>{s.scope === 'locations' ? 'Lokationer' : 'Varer'}</td>
                <td>{s.kind === 'opening' ? 'Åbningsbalance' : 'Cyklisk'}</td>
                <td><Badge tone={statusTone(s.status)}>{statusLabel[s.status]}</Badge></td>
                <td className="text-right tabular-nums">{s.lineCount}</td>
                <td className="text-right tabular-nums">{s.countedLines}</td>
                <td className="text-right tabular-nums">{s.varianceLines}</td>
                <td className="whitespace-nowrap">{fmtTime(s.createdAt)} · {s.createdBy}</td>
                <td className="whitespace-nowrap">{fmtTime(s.postedAt)}</td>
              </tr>
            ))}
            {list.data?.length === 0 && <tr><td colSpan={10} className="text-zinc-500">Ingen optællinger endnu.</td></tr>}
          </tbody>
        </table>
      )}
      {creating && <CreateCountModal onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); void navigate({ to: '/counts/$id', params: { id } }); }} onError={setError} />}
    </div>
  );
}

function CreateCountModal({ onClose, onCreated, onError }: { onClose: () => void; onCreated: (id: string) => void; onError: (e: unknown) => void }) {
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  const [form, setForm] = useState({ warehouseId: '', name: '', scope: 'locations', kind: 'cycle', locationIds: [] as string[] });
  useEffect(() => {
    if (!form.warehouseId && warehouses.data?.length) setForm((f) => ({ ...f, warehouseId: warehouses.data![0]!.id }));
  }, [warehouses.data, form.warehouseId]);
  const locations = useQuery({ queryKey: ['locations', form.warehouseId], queryFn: () => get<{ items: LocationDto[] }>(`/locations?warehouseId=${form.warehouseId}&perPage=500`), enabled: !!form.warehouseId && form.scope === 'locations' });
  const create = useMutation({
    mutationFn: () => post<CountSessionDetailDto>('/counts', { warehouseId: form.warehouseId, name: form.name, scope: form.scope, kind: form.kind, locationIds: form.scope === 'locations' && form.locationIds.length ? form.locationIds : undefined }),
    onSuccess: (s) => onCreated(s.id),
    onError,
  });
  return (
    <Modal title="Ny optælling" onClose={onClose}>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="label">Navn</label>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Åbningsoptælling BB" />
        </div>
        <div>
          <label className="label">Lager</label>
          <select className="input" value={form.warehouseId} onChange={(e) => setForm({ ...form, warehouseId: e.target.value, locationIds: [] })}>
            {warehouses.data?.map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Art</label>
          <select className="input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="cycle">Cyklisk optælling (bogføres som optælling)</option>
            <option value="opening">Åbningsbalance (bogføres som migrering)</option>
          </select>
        </div>
        <div>
          <label className="label">Omfang</label>
          <select className="input" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
            <option value="locations">Lokationer (hele lageret eller udvalgte)</option>
          </select>
        </div>
        {form.scope === 'locations' && (
          <div className="md:col-span-2">
            <label className="label">Lokationer (ingen valgt = hele lageret)</label>
            <select multiple className="input h-40" value={form.locationIds} onChange={(e) => setForm({ ...form, locationIds: [...e.target.selectedOptions].map((o) => o.value) })}>
              {locations.data?.items.map((l) => <option key={l.id} value={l.id}>{l.code} ({l.onHand})</option>)}
            </select>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-zinc-500">Linjerne dannes fra den nuværende beholdning. Varer på lokationer uden beholdning tilføjes undervejs med SKU og lokationskode.</p>
      <div className="mt-4 flex justify-end gap-2">
        <button className="btn" onClick={onClose}>Annullér</button>
        <button className="btn btn-primary" disabled={!form.name || !form.warehouseId || create.isPending} onClick={() => create.mutate()}>Opret</button>
      </div>
    </Modal>
  );
}

/** Enter counted quantities, add found stock, post with a variance overview. */
export function CountDetailPage() {
  const { id } = useParams({ from: '/app/counts/$id' });
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const detail = useQuery({ queryKey: ['count', id], queryFn: () => get<CountSessionDetailDto>(`/counts/${id}`) });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [add, setAdd] = useState({ sku: '', locationCode: '', counted: '' });
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['count', id] }); void qc.invalidateQueries({ queryKey: ['counts'] }); void qc.invalidateQueries({ queryKey: ['stock'] }); };
  const enter = useMutation({
    mutationFn: (body: { lines?: { lineId: string; counted: number | null }[]; add?: { sku: string; locationCode: string; counted: number }[] }) => post(`/counts/${id}/enter`, { lines: body.lines ?? [], add: body.add ?? [] }),
    onSuccess: () => { setDraft({}); setAdd({ sku: '', locationCode: '', counted: '' }); invalidate(); },
    onError: setError,
  });
  const postCount = useMutation({ mutationFn: () => post(`/counts/${id}/post`), onSuccess: invalidate, onError: setError });
  const cancel = useMutation({ mutationFn: () => post(`/counts/${id}/cancel`), onSuccess: invalidate, onError: setError });
  const s = detail.data;
  if (!s) return <Spinner />;
  const open = s.status === 'open' && can('stock.count');
  const dirty = Object.entries(draft).filter(([lineId, v]) => v !== String(s.lines.find((l) => l.id === lineId)?.counted ?? ''));
  const saveDraft = () => enter.mutate({ lines: dirty.map(([lineId, v]) => ({ lineId, counted: v === '' ? null : Number(v) })) });
  const fillNavision = () => {
    const next: Record<string, string> = {};
    for (const l of s.lines) if (l.counted === null && l.navisionQty !== null) next[l.id] = String(Math.max(0, l.navisionQty));
    setDraft({ ...draft, ...next });
  };
  const fillExpected = () => {
    const next: Record<string, string> = {};
    for (const l of s.lines) if (l.counted === null) next[l.id] = String(l.expected);
    setDraft({ ...draft, ...next });
  };

  return (
    <div className="space-y-4">
      <PageTitle
        title={`${s.name} · ${s.warehouseCode}`}
        actions={
          <>
            <Link to="/counts" className="btn">Tilbage</Link>
            {open && s.kind === 'opening' && <button className="btn" onClick={fillNavision}>Udfyld med Navision</button>}
            {open && <button className="btn" onClick={fillExpected}>Udfyld med forventet</button>}
            {open && <button className="btn btn-primary" disabled={dirty.length === 0 || enter.isPending} onClick={saveDraft}>Gem tællinger ({dirty.length})</button>}
            {open && <button className="btn" onClick={() => confirm('Annullér optællingen?') && cancel.mutate()}>Annullér</button>}
            {open && <button className="btn btn-danger" disabled={s.countedLines === 0 || postCount.isPending} onClick={() => confirm(`Bogfør ${s.countedLines} talte linjer med ${s.varianceLines} afvigelser?`) && postCount.mutate()}>Bogfør</button>}
          </>
        }
      />
      <ErrorBox error={error} />
      <div className="flex flex-wrap gap-3 text-sm">
        <Badge tone={statusTone(s.status)}>{statusLabel[s.status]}</Badge>
        <span>{s.kind === 'opening' ? 'Åbningsbalance' : 'Cyklisk'}</span>
        <span>{s.countedLines} af {s.lineCount} talt</span>
        <span>{s.varianceLines} afvigelser</span>
        {s.postedAt && <span>bogført {fmtTime(s.postedAt)}</span>}
      </div>

      {open && (
        <div className="card flex flex-wrap items-end gap-3 text-sm">
          <div><label className="label">Tilføj fundet vare: SKU</label><input className="input w-40" value={add.sku} onChange={(e) => setAdd({ ...add, sku: e.target.value })} /></div>
          <div><label className="label">Lokation</label><input className="input w-32" value={add.locationCode} onChange={(e) => setAdd({ ...add, locationCode: e.target.value.toUpperCase() })} /></div>
          <div><label className="label">Talt</label><input className="input w-24" type="number" min={0} value={add.counted} onChange={(e) => setAdd({ ...add, counted: e.target.value })} /></div>
          <button className="btn" disabled={!add.sku || !add.locationCode || add.counted === '' || enter.isPending} onClick={() => enter.mutate({ add: [{ sku: add.sku, locationCode: add.locationCode, counted: Number(add.counted) }] })}>Tilføj</button>
        </div>
      )}

      <table className="table card p-0">
        <thead><tr><th>Lokation</th><th>SKU</th><th>Vare</th><th className="text-right">Forventet</th><th className="text-right" title="Navision-beholdning (alle placeringer)">Navision</th><th className="text-right w-32">Talt</th><th className="text-right">Afvigelse</th><th>Talt af</th></tr></thead>
        <tbody>
          {s.lines.map((l) => {
            const value = draft[l.id] ?? (l.counted === null ? '' : String(l.counted));
            const variance = value === '' ? null : Number(value) - l.expected;
            return (
              <tr key={l.id}>
                <td className="font-medium">{l.locationCode}</td>
                <td>{l.sku}</td>
                <td className="text-zinc-600">{l.productTitle}{l.variantTitle && ` · ${l.variantTitle}`}</td>
                <td className="text-right tabular-nums">{l.expected}</td>
                <td className="text-right tabular-nums text-zinc-500">{l.navisionQty ?? '–'}</td>
                <td className="text-right">
                  {open ? <input className="input text-right" type="number" min={0} value={value} onChange={(e) => setDraft({ ...draft, [l.id]: e.target.value })} /> : <span className="tabular-nums">{l.counted ?? '–'}</span>}
                </td>
                <td className={`text-right tabular-nums ${variance ? (variance < 0 ? 'text-red-700' : 'text-green-700') : ''}`}>{variance === null ? '' : variance > 0 ? `+${variance}` : variance}</td>
                <td className="text-xs text-zinc-500">{l.countedBy}{l.countedAt && ` · ${fmtTime(l.countedAt)}`}</td>
              </tr>
            );
          })}
          {s.lines.length === 0 && <tr><td colSpan={8} className="text-zinc-500">Ingen linjer. Tilføj fundne varer ovenfor.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
